/*
  An uncontrolled input that stays live — except while somebody is typing in it.

  THE PROBLEM THIS REPLACES. Three editors reached for `defaultValue={value}` plus `key={value}`: the box
  is uncontrolled while you type, so a round trip mid-edit cannot yank the caret, and changing the key
  remounts it when the committed value moves. That second half is the defect. React does not "refresh" a
  keyed element — it destroys the DOM node and builds a new one. So when a colleague commits a change to
  the same field you are editing, the box you are typing in is thrown away: your half-typed number is
  gone, the caret is gone, and nothing says why. On a modal whose whole purpose is repricing a deal, the
  keystroke that vanishes is a number somebody was about to commit.

  WHY NOT JUST DROP THE KEY. Then the box never resyncs at all: `defaultValue` is read once, so a
  colleague's change would never appear and you would be reading a stale number with no indication of it.
  Both halves are needed — resync when the value moves, and never while the reader is mid-edit.

  WHY BLUR NEEDS A BASELINE, WHICH IS THE PART THAT IS EASY TO GET WRONG. "Leave it alone while focused"
  on its own trades one silent loss for another. Focus a box showing 6, type nothing, let a colleague set
  it to 9, then click away: the box still holds "6", every commit path compares against the CURRENT value,
  6 !== 9, and blurring silently reverts your colleague. So focus records what the field held, and blur
  asks whether you actually edited it. If you did, your edit wins — last write wins is this app's model
  everywhere else, and P5f already tells you a colleague touched what you have open. If you did not, there
  is no edit to commit and the field adopts their value instead.
*/
import { useEffect, useRef, type RefObject } from 'react';

export interface LiveInput {
  /** Put this on the input. The resync writes through it. */
  ref: RefObject<HTMLInputElement>;
  /** Records what the field held, so blur can tell an edit from a visit. Pass as `onFocus`. */
  onFocus(): void;
  /**
   * Call first in `onBlur`. Returns whether there is an edit to commit.
   *
   * False means the reader typed nothing — the field has been resynced to the current value and the
   * caller must dispatch nothing. True means the text in the box is theirs; read it and commit as before.
   */
  settle(): boolean;
}

export function useLiveInput(value: string): LiveInput {
  const ref = useRef<HTMLInputElement>(null);
  /** What the field held when focus was taken. Null while unfocused, which is also the initial state. */
  const baseline = useRef<string | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // The reader is in it. Their text stands until they leave, and `settle` decides what happens then.
    if (document.activeElement === el) return;
    el.value = value;
  }, [value]);

  return {
    ref,
    onFocus: () => {
      baseline.current = ref.current?.value ?? null;
    },
    settle: () => {
      const el = ref.current;
      const was = baseline.current;
      baseline.current = null;
      if (!el) return false;
      if (was !== null && el.value === was) {
        // A visit, not an edit. Adopt whatever the value is now — which is the colleague's, if one landed.
        el.value = value;
        return false;
      }
      return true;
    },
  };
}

/**
 * The input itself, so ten sites get one answer instead of ten.
 *
 * A component rather than only a hook, because `useLiveInput` cannot be called inside the `map` most of
 * these live in, and because writing the ref/onFocus/settle triple out by hand ten times is ten chances
 * to leave one off — which is exactly how the pattern spread in the first place.
 *
 * `onCommit` is handed the ELEMENT as well as the text, because half these fields clamp: the person
 * panel bounds a stranded month to 1–36 and writes the bounded number straight back so the field never
 * states a month the model does not hold. That write is theirs to make, not this component's.
 */
export function LiveField({
  value,
  onCommit,
  onInput,
  escapeRestores = true,
  ...rest
}: {
  value: string;
  onCommit(text: string, el: HTMLInputElement): void;
  /** For the assumptions editor's live totals, which track every keystroke rather than the commit. */
  onInput?: (text: string, el: HTMLInputElement) => void;
  /** Escape puts the stored value back and leaves. On everywhere; named so a site can say otherwise. */
  escapeRestores?: boolean;
  id?: string;
  'data-testid'?: string;
  type?: string;
  min?: number;
  max?: number;
  step?: number | string;
  placeholder?: string;
  list?: string;
  title?: string;
  disabled?: boolean;
}): JSX.Element {
  const live = useLiveInput(value);
  return (
    <input
      {...rest}
      ref={live.ref}
      defaultValue={value}
      onFocus={live.onFocus}
      onInput={onInput ? (e) => onInput(e.currentTarget.value, e.currentTarget) : undefined}
      onBlur={(e) => {
        // Nothing typed is nothing to commit. Without this, leaving a field you only clicked into would
        // write its old contents back over a change that landed while you were sitting in it.
        if (!live.settle()) return;
        onCommit(e.target.value, e.target);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        if (e.key === 'Escape' && escapeRestores) {
          e.currentTarget.value = value;
          e.currentTarget.blur();
        }
      }}
    />
  );
}
